<template>
  <page-template :is-loading="isLoading" :data-exists="actions !== null">
    <template #pageHeadline>
      {{ $t('action.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="actions"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="actions-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewActionPath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateAction"
        @delete-row-button-clicked="openDeleteActionModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadActions"
      />
      <rbz-modal-dialog
        :show="showDeleteActionModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteAction"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteActionModal = false"
        @closeModal="showDeleteActionModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedAction.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { Action, DocumentName } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ActionsList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('action.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getActionPath(item);
          },
        },
        {
          id: 'type',
          title: this.$t('action.listPage.type'),
          fieldNames: ['type'],
          displayFunction: (item: Action) => {
            return this.$t(`action.typeValue.${item.type}`);
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-180px',
          cellContentClasses: 'is-capitalized',
        },
        {
          id: 'status-code',
          title: this.$t('action.statusCode'),
          fieldNames: ['params'],
          displayFunction: (item: Action) => {
            return item.params?.status || '';
          },
          isSortable: true,
          isSearchable: true,
          isNumber: true,
          classes: 'width-170px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getActionPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      actions: null as Action[],
      pageLoadingCounter: null,

      showDeleteActionModal: false,
      selectedAction: null,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Actions List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadActions();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('action.plural'),
        singular: this.$t('action.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/actions/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/actions`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      let response;
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const aclProfiles = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/global-filters`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const globalFilters = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/rate-limit-rules`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const rateLimitRules = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const contentFilterProfiles = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/dynamic-rules`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const dynamicRules = response?.data?.items || [];
      this.referencedIds = _.uniq([
        ..._.map(aclProfiles, 'action'),
        ..._.map(globalFilters, 'action'),
        ..._.map(rateLimitRules, 'action'),
        ..._.map(contentFilterProfiles, 'action'),
        ..._.map(dynamicRules, 'action'),
      ]);
    },

    isDeleteDisabled(action: Action) {
      return this.isActionProtected(action) || this.isActionReferenced(action);
    },

    isActionProtected(action: Action): boolean {
      return action.id.startsWith('action-');
    },

    isActionReferenced(action: Action): boolean {
      return this.referencedIds.includes(action.id);
    },

    deleteTooltip(action: Action): string {
      if (this.isActionProtected(action)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isActionReferenced(action)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getActionPath(action: Action) {
      return this.getEditPath('actions', action.id);
    },

    editAction(action: Action) {
      this.navigateTo(this.getActionPath(action));
    },

    async duplicateAction(action: Action) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions/${action.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalAction = getOriginalResponse.data;
      const actionToAdd = _.cloneDeep(originalAction);
      actionToAdd.id = DatasetsUtils.generateUUID2();
      actionToAdd.name = Utils.generateUniqueEntityName(
        actionToAdd.name,
        _.map(this.actions, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/actions/${actionToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: actionToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editAction(actionToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteAction() {
      this.setPageLoadingStatus(true);
      const actionId = this.selectedAction.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/actions/${actionId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.actions = _.filter(this.actions, (action) => {
          return action.id !== actionId;
        });
      }
      this.showDeleteActionModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('actions', 'json', this.actions);
    },

    openDeleteActionModal(action: Action) {
      this.showDeleteActionModal = true;
      this.selectedAction = action;
    },

    getNewActionPath() {
      return this.getNewPath('actions');
    },
  },
});
</script>
